<template>
    <div id="archive-filter-bar">
        <div class="filter-section">
            <div class="filter-toolbar">
                <!-- Filter Modal Button -->
                <div class="filter-button">
                    <button
                        class="custom-filter-button"
                        :class="{ 'active-filter': activeFilterCount > 0 }"
                        @click="toggleFilterModal"
                    >
                        <span class="material-icons">tune</span>
                        <span class="button-text">{{ $t("message.filters") }}</span>
                        <span v-if="activeFilterCount > 0" class="filter-count">{{ activeFilterCount }}</span>
                    </button>

                    <!-- Filter Panel -->
                    <div v-if="isFilterModalOpen" class="filter-panel" ref="filterPanel">
                        <div class="filter-panel-arrow"></div>
                        <div class="filter-panel-content">
                            <div class="filter-header">
                                <h3>{{ $t("message.filteringOptions") }}</h3>
                                <div class="filter-header-actions">
                                    <a-button type="link" class="reset-button" @click="resetFilters">
                                        {{ $t("message.reset") }}
                                    </a-button>
                                    <a-button type="primary" class="apply-button" @click="applyFilters">
                                        {{ $t("message.apply") }}
                                    </a-button>
                                    <button @click="closeFilterModal" class="close-button">
                                        <span class="material-icons">close</span>
                                    </button>
                                </div>
                            </div>

                            <div class="filter-tab-layout">
                                <!-- Sol tab menü -->
                                <div class="filter-tabs">
                                    <div
                                        v-for="tab in filterTabs"
                                        :key="tab.key"
                                        :class="['filter-tab', { 'active': activeFilterTab === tab.key }]"
                                        @click="activeFilterTab = tab.key"
                                    >
                                        {{ tab.title }}
                                        <span v-if="hasActiveFilterInTab(tab.key)" class="tab-filter-indicator"></span>
                                    </div>
                                </div>

                                <!-- Sağ içerik alanı -->
                                <div class="filter-tab-content">
                                    <!-- Hızlı Filtreler -->
                                    <div v-show="activeFilterTab === 'quickFilters'" class="filter-tab-pane">
                                        <!-- Tarih Aralığı -->
                                        <div class="filter-options">
                                            <h4>{{ $t("message.dateRange") }}</h4>
                                            <div class="custom-date-inputs">
                                                <div class="date-input-group">
                                                    <label>{{ $t("message.startDateLabel") }}</label>
                                                    <datetime
                                                        v-model="localStartDate"
                                                        :max-datetime="maxDate"
                                                        :min-datetime="lastFiveYear"
                                                        type="datetime"
                                                        input-class="form-control"
                                                    />
                                                </div>
                                                <div class="date-input-group">
                                                    <label>{{ $t("message.endDateLabel") }}</label>
                                                    <datetime
                                                        v-model="localEndDate"
                                                        :max-datetime="maxDate"
                                                        :min-datetime="localStartDate"
                                                        type="datetime"
                                                        input-class="form-control"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div class="filter-options">
                                            <h4>{{ $t("message.quickFilters") }}</h4>
                                            <div class="quick-filters-grid">
                                                <div
                                                    :class="[
                                                        'quick-filter-card',
                                                        { 'selected': localFilterDescription }
                                                    ]"
                                                    @click="toggleFilter('description')"
                                                >
                                                    <div class="filter-icon-wrapper">
                                                        <md-icon>comment</md-icon>
                                                    </div>
                                                    <span class="filter-text">{{ $t('message.showFeedbackHistory') }}</span>
                                                </div>
                                                <div
                                                    :class="[
                                                        'quick-filter-card',
                                                        { 'selected': localFilterVideoCall }
                                                    ]"
                                                    @click="toggleFilter('videoCall')"
                                                >
                                                    <div class="filter-icon-wrapper">
                                                        <md-icon>videocam</md-icon>
                                                    </div>
                                                    <span class="filter-text">{{ $t('message.filterVideoCall') }}</span>
                                                </div>
                                                <div
                                                    :class="[
                                                        'quick-filter-card',
                                                        { 'selected': localFilterLostChats }
                                                    ]"
                                                    @click="toggleFilter('lostChats')"
                                                >
                                                    <div class="filter-icon-wrapper">
                                                        <md-icon>phone_missed</md-icon>
                                                    </div>
                                                    <span class="filter-text">{{ $t('message.filterLostChat') }}</span>
                                                </div>
                                                <div
                                                    :class="[
                                                        'quick-filter-card',
                                                        { 'selected': localFilterClosedByAgent }
                                                    ]"
                                                    @click="toggleFilter('closedByAgent')"
                                                >
                                                    <div class="filter-icon-wrapper">
                                                        <md-icon>support_agent</md-icon>
                                                    </div>
                                                    <span class="filter-text">{{ $t('message.filterClosedByAgent') }}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Siteler -->
                                        <div class="filter-options">
                                            <h4>{{ $t("message.siteSelection") }}</h4>
                                            <div class="custom-select-wrapper">
                                                <div 
                                                    class="custom-select" 
                                                    @click.stop="toggleSiteDropdown"
                                                    :class="{ 'open': isSiteDropdownOpen }"
                                                >
                                                    <span class="selected-value">
                                                        {{ localSelectedSite || $t("message.allSites") }}
                                                    </span>
                                                    <span class="dropdown-arrow"></span>
                                                </div>
                                                <div v-if="isSiteDropdownOpen" class="custom-dropdown">
                                                    <div 
                                                        class="dropdown-item"
                                                        :class="{ 'selected': !localSelectedSite }"
                                                        @click.stop="selectSite(null)"
                                                    >
                                                        {{ $t("message.allSites") }}
                                                    </div>
                                                    <div 
                                                        v-for="site in customerDomains" 
                                                        :key="site"
                                                        class="dropdown-item"
                                                        :class="{ 'selected': localSelectedSite === site }"
                                                        @click.stop="selectSite(site)"
                                                    >
                                                        {{ site }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Chat Puanı -->
                                        <div class="filter-options">
                                            <h4>{{ $t("message.chatScore") }}</h4>
                                            <div class="rating-filter">
                                                <a-rate v-model="localPoint" :allow-clear="true" />
                                                <button
                                                    class="question-button"
                                                    @click="clearRating"
                                                    v-tippy="{ followCursor: true, placement: 'bottom' }"
                                                    :content="$t('message.archiveAllRated')"
                                                >
                                                    ?
                                                </button>
                                            </div>
                                        </div>
                                    </div>



                                    <!-- Platform Seçimi -->
                                    <div v-show="activeFilterTab === 'platforms'" class="filter-tab-pane">
                                        <div class="filter-options">
                                            <h4>{{ $t("message.platformSelection") }}</h4>
                                            <div class="platform-grid">
                                                <div
                                                    v-for="platform in multiSelectOptions"
                                                    :key="platform.name"
                                                    :class="[
                                                        'platform-card',
                                                        { 'selected': isPlatformSelected(platform.name) }
                                                    ]"
                                                    @click="togglePlatform(platform.name)"
                                                >
                                                    <div class="platform-icon">
                                                        <WhatsappIcon 
                                                            v-if="platform.name === 'Whatsapp'" 
                                                            class="platform-icon-svg" 
                                                            color="#25D366" 
                                                        />
                                                        <InstagramIcon 
                                                            v-else-if="platform.name === 'Instagram'" 
                                                            class="platform-icon-svg" 
                                                            special-background-mode 
                                                        />
                                                        <TelegramIcon 
                                                            v-else-if="platform.name === 'Telegram'" 
                                                            class="platform-icon-svg" 
                                                        />
                                                        <SupsisIcon 
                                                            v-else-if="platform.name === 'Supsis'" 
                                                            class="platform-icon-svg" 
                                                        />
                                                        <MessengerIcon 
                                                            v-else-if="platform.name === 'Messenger'" 
                                                            class="platform-icon-svg" 
                                                        />
                                                        <TrendyolIcon 
                                                            v-else-if="platform.name === 'Trendyol'" 
                                                            class="platform-icon-svg" 
                                                        />
                                                    </div>
                                                    <div class="platform-name">{{ platform.name }}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Sohbet Başlatan Seçimi -->
                                    <div v-show="activeFilterTab === 'starters'" class="filter-tab-pane">
                                        <div class="filter-options">
                                            <h4>{{ $t("message.chatStarter") }}</h4>
                                            <div class="destroyer-grid">
                                                <div
                                                    v-for="starter in starterMultiSelectOptions"
                                                    :key="'starter-' + starter.name"
                                                    :class="[
                                                        'destroyer-card',
                                                        { 'selected': isStarterSelected(starter.name) }
                                                    ]"
                                                    @click="toggleStarter(starter.name)"
                                                >
                                                    <div class="destroyer-icon-wrapper">
                                                        <md-icon v-if="starter.name === 'user'">people</md-icon>
                                                        <md-icon v-else-if="starter.name === 'visitor'">person</md-icon>
                                                        <md-icon v-else-if="starter.name === 'api'">api</md-icon>
                                                        <md-icon v-else-if="starter.name === 'broadcast'">campaign</md-icon>
                                                        <md-icon v-else-if="starter.name === 'instagram-comment'">chat</md-icon>
                                                        <md-icon v-else-if="starter.name === 'page-comment'">chat_bubble</md-icon>
                                                        <md-icon v-else-if="starter.name === 'instagram-mention'">alternate_email</md-icon>
                                                        <md-icon v-else-if="starter.name === 'trendyol'">storefront</md-icon>
                                                        <md-icon v-else-if="starter.name === 'automation'">bolt</md-icon>
                                                        <md-icon v-else-if="starter.name === 'voice-agent-call'">call</md-icon>
                                                        <md-icon v-else>chat</md-icon>
                                                    </div>
                                                    <div class="destroyer-name">
                                                        <template v-if="starter.name === 'user'">{{ $t("archive.startedBy.user") }}</template>
                                                        <template v-else-if="starter.name === 'visitor'">{{ $t("archive.startedBy.visitor") }}</template>
                                                        <template v-else-if="starter.name === 'api'">{{ $t("archive.startedBy.api") }}</template>
                                                        <template v-else-if="starter.name === 'broadcast'">{{ $t("archive.startedBy.broadcast") }}</template>
                                                        <template v-else-if="starter.name === 'instagram-comment'">{{ $t("archive.startedBy.instagram-comment") }}</template>
                                                        <template v-else-if="starter.name === 'page-comment'">{{ $t("archive.startedBy.page-comment") }}</template>
                                                        <template v-else-if="starter.name === 'instagram-mention'">{{ $t("archive.startedBy.instagram-mention") }}</template>
                                                        <template v-else-if="starter.name === 'trendyol'">{{ $t("archive.startedBy.trendyol") }}</template>
                                                        <template v-else-if="starter.name === 'automation'">{{ $t("archive.startedBy.automation") }}</template>
                                                        <template v-else-if="starter.name === 'voice-agent-call'">{{ $t("archive.startedBy.voice-agent-call") }}</template>
                                                        <template v-else>{{ starter.name }}</template>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Destroyer Seçimi -->
                                    <div v-show="activeFilterTab === 'destroyers'" class="filter-tab-pane">
                                        <div class="filter-options">
                                            <h4>{{ $t("message.chatCloser") }}</h4>
                                            <div class="destroyer-grid">
                                                <div
                                                    v-for="destroyer in destroyerMultiSelectOptions"
                                                    :key="'destroyer-' + destroyer.name"
                                                    :class="[
                                                        'destroyer-card',
                                                        { 'selected': isDestroyerSelected(destroyer.name) }
                                                    ]"
                                                    @click="toggleDestroyer(destroyer.name)"
                                                >
                                                    <div class="destroyer-icon-wrapper">
                                                        <md-icon v-if="destroyer.name === 'Agent'">people</md-icon>
                                                        <md-icon v-else-if="destroyer.name === 'Visitor'">person</md-icon>
                                                        <md-icon v-else-if="destroyer.name === 'System'">settings_suggest</md-icon>
                                                        <md-icon v-else-if="destroyer.name === 'Chatbot'">smart_toy</md-icon>
                                                    </div>
                                                    <div class="destroyer-name">{{ $t(`archive.destroyedBy.${destroyer.name.toLowerCase()}`) }}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Etiketler -->
                                    <div v-show="activeFilterTab === 'tags'" class="filter-tab-pane">
                                        <div class="filter-options">
                                            <h4>{{ $t('message.tags') }}</h4>
                                            <multiselect
                                                v-model="localTagsMultiSelectValue"
                                                :multiple="true"
                                                :options="allTags"
                                                :placeholder="$t('message.tags')"
                                                label="name"
                                                track-by="name"
                                            >
                                                <template slot="option" slot-scope="props">
                                                    <div class="tag-option">
                                                        <div
                                                            class="tag-color"
                                                            :style="{ background: props.option.color }"
                                                        ></div>
                                                        <span class="option__title">{{ props.option.name }}</span>
                                                    </div>
                                                </template>
                                            </multiselect>
                                        </div>
                                    </div>



                                    <!-- Kullanıcı Seçimi -->
                                    <div v-show="activeFilterTab === 'users'" class="filter-tab-pane">
                                        <div class="filter-options">
                                            <h4>{{ $t("archive.userSelectionTitle") }}</h4>
                                            <div class="user-search-wrapper">
                                                <div class="user-search-input">
                                                    <md-icon class="search-icon">search</md-icon>
                                                    <input 
                                                        type="text" 
                                                        :placeholder="$t('archive.userSearchPlaceholder')" 
                                                        v-model="userSearchQuery"
                                                        class="search-input"
                                                    />
                                                </div>
                                            </div>
                                            <div class="users-list">
                                                <div 
                                                    v-for="user in filteredUsers" 
                                                    :key="user.id"
                                                    :class="[
                                                        'user-item',
                                                        { 'selected': localSelectedUser === user.id }
                                                    ]"
                                                    @click="selectUser(user.id)"
                                                >
                                                    <div class="user-avatar">
                                                        <span v-if="user.id === null" class="material-icons">group</span>
                                                        <span v-else-if="user.id === 'only-chatbot'" class="material-icons">smart_toy</span>
                                                        <span v-else class="user-initial">{{ getUserInitial(user.label) }}</span>
                                                    </div>
                                                    <div class="user-info">
                                                        <div class="user-name">{{ user.label }}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>


                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import Multiselect from "vue-multiselect";
import WhatsappIcon from "../../views/Pages/Chatbot/pages/integrations/svg/WhatsappIcon.vue";
import InstagramIcon from "../../views/Pages/Chatbot/pages/integrations/svg/InstagramIcon.vue";
import TelegramIcon from "../../views/Pages/Chatbot/pages/integrations/svg/TelegramIcon.vue";
import SupsisIcon from "../../views/Pages/Chatbot/pages/integrations/svg/SupsisIcon.vue";
import MessengerIcon from "../../views/Pages/Chatbot/pages/integrations/svg/MessengerIcon.vue";
import TrendyolIcon from "../../views/Pages/Chatbot/pages/integrations/svg/TrendyolIcon.vue";
import Chatbot from "../svg/Chatbot.vue";
import CustomPicker from "../pickers/CustomPicker.vue";
import TimeUtils from "../../utils/TimeUtils";
import { mapState } from "vuex";
import { Datetime } from "vue-datetime";

const refTypes = ["Whatsapp", "Supsis", "Instagram", "Telegram", "Messenger", "Trendyol"];
const starterTypes = [
    "user",
    "visitor",
    "api",
    "broadcast",
    "instagram-comment",
    "page-comment",
    "instagram-mention",
    "trendyol",
    "automation",
    "voice-agent-call"
];
const destroyerTypes = [
    "Agent",
    "Visitor",
    "Chatbot",
    "System"
];

export default {
    name: "ArchiveFilterBar",
    components: {
        Multiselect,
        WhatsappIcon,
        InstagramIcon,
        TelegramIcon,
        SupsisIcon,
        MessengerIcon,
        TrendyolIcon,
        Chatbot,
        CustomPicker,
        Datetime,
    },
    props: {
        query: {
            type: String,
            default: "",
        },
        startDate: {
            type: String,
            default: null,
        },
        endDate: {
            type: String,
            default: null,
        },
        selectedUser: {
            type: String,
            default: null,
        },
        selectedSite: {
            type: String,
            default: null,
        },
        multiSelectValue: {
            type: Array,
            default: () => [],
        },
        destroyerMultiSelectValue: {
            type: Array,
            default: () => [],
        },
        starterMultiSelectValue: {
            type: Array,
            default: () => [],
        },
        tagsMultiSelectValue: {
            type: Array,
            default: () => [],
        },
        point: {
            type: Number,
            default: undefined,
        },
        filterDescription: {
            type: Boolean,
            default: false,
        },
        filterVideoCall: {
            type: Boolean,
            default: false,
        },
        filterLostChats: {
            type: Boolean,
            default: false,
        },
        filterClosedByAgent: {
            type: Boolean,
            default: false,
        },
        timeSelect: {
            type: String,
            default: "today",
        },
        usersFilter: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            isFilterModalOpen: false,
            activeFilterTab: "quickFilters",
            localQuery: this.query,
            localStartDate: this.startDate,
            localEndDate: this.endDate,
            localSelectedUser: this.selectedUser,
            localSelectedSite: this.selectedSite,
            localMultiSelectValue: [...this.multiSelectValue],
            localDestroyerMultiSelectValue: [...this.destroyerMultiSelectValue],
            localStarterMultiSelectValue: [...this.starterMultiSelectValue],
            localTagsMultiSelectValue: [...this.tagsMultiSelectValue],
            localPoint: this.point,
            localTimeSelect: this.timeSelect,
            localFilterDescription: this.filterDescription,
            localFilterVideoCall: this.filterVideoCall,
            localFilterLostChats: this.filterLostChats,
            localFilterClosedByAgent: this.filterClosedByAgent,
            multiSelectOptions: refTypes.map((x) => ({ name: x })),
            starterMultiSelectOptions: starterTypes.map((x) => ({ name: x })),
            destroyerMultiSelectOptions: destroyerTypes.map((x) => ({ name: x })),
            maxDate: moment().toISOString(),
            lastFiveYear: moment().subtract(5, "year").toISOString(),
            userSearchQuery: "",
            isSiteDropdownOpen: false,
        };
    },
    computed: {
        ...mapState({
            allTags: (state) => state.tag.tags.data.map((x) => ({ name: x.name, color: x.color, value: x._id })),
            customerDomains: (state) => state.visitor.customerDomains,
        }),
        filterTabs() {
            return [
                { key: "quickFilters", title: this.$t("message.quickFilters") },
                { key: "platforms", title: this.$t("message.platforms") },
                { key: "users", title: this.$t("message.users") },
                { key: "starters", title: this.$t("message.chatStarter") },
                { key: "destroyers", title: this.$t("message.chatCloser") },
                { key: "tags", title: this.$t("message.tags") },
            ];
        },
        activeFilterCount() {
            let count = 0;
            if (this.filterDescription) count++;
            if (this.filterVideoCall) count++;
            if (this.filterLostChats) count++;
            if (this.filterClosedByAgent) count++;
            if (this.startDate && this.endDate) count++;
            if (this.multiSelectValue.length > 0) count++;
            if (this.destroyerMultiSelectValue.length > 0) count++;
            if (this.tagsMultiSelectValue.length > 0) count++;
            if (this.selectedSite) count++;
            if (this.selectedUser) count++;
            if (this.point !== undefined && this.point !== null) count++;
            return count;
        },
        filteredUsers() {
            const users = this.getAgentsOptions();
            if (!this.userSearchQuery) {
                return users;
            }
            return users.filter(user => 
                user.label.toLowerCase().includes(this.userSearchQuery.toLowerCase())
            );
        },
    },
    methods: {
        toggleFilter(filterType) {
            switch (filterType) {
                case 'description':
                    this.localFilterDescription = !this.localFilterDescription;
                    break;
                case 'videoCall':
                    this.localFilterVideoCall = !this.localFilterVideoCall;
                    break;
                case 'lostChats':
                    this.localFilterLostChats = !this.localFilterLostChats;
                    break;
                case 'closedByAgent':
                    this.localFilterClosedByAgent = !this.localFilterClosedByAgent;
                    break;
            }
        },
        toggleFilterModal() {
            this.isFilterModalOpen = !this.isFilterModalOpen;
        },
        closeFilterModal() {
            this.isFilterModalOpen = false;
        },
        setCustomDateFilter(filter) {
            this.localTimeSelect = filter;
        },
        getLast11Months() {
            return TimeUtils.getLast11Months();
        },
        togglePlatform(platformName) {
            const existingIndex = this.localMultiSelectValue.findIndex(p => p.name === platformName);
            if (existingIndex >= 0) {
                this.localMultiSelectValue.splice(existingIndex, 1);
            } else {
                this.localMultiSelectValue.push({ name: platformName });
            }
        },
        isPlatformSelected(platformName) {
            return this.localMultiSelectValue.some(p => p.name === platformName);
        },
        toggleDestroyer(destroyerName) {
            const existingIndex = this.localDestroyerMultiSelectValue.findIndex(d => d.name === destroyerName);
            if (existingIndex >= 0) {
                this.localDestroyerMultiSelectValue.splice(existingIndex, 1);
            } else {
                this.localDestroyerMultiSelectValue.push({ name: destroyerName });
            }
        },
        isDestroyerSelected(destroyerName) {
            return this.localDestroyerMultiSelectValue.some(d => d.name === destroyerName);
        },
        toggleStarter(starterName) {
            const existingIndex = this.localStarterMultiSelectValue.findIndex(s => s.name === starterName);
            if (existingIndex >= 0) {
                this.localStarterMultiSelectValue.splice(existingIndex, 1);
            } else {
                this.localStarterMultiSelectValue.push({ name: starterName });
            }
        },
        isStarterSelected(starterName) {
            return this.localStarterMultiSelectValue.some(s => s.name === starterName);
        },
        selectUser(userId) {
            this.localSelectedUser = userId;
        },
        getUserInitial(name) {
            if (!name) return '';
            return name.charAt(0).toUpperCase();
        },
        toggleSiteDropdown() {
            this.isSiteDropdownOpen = !this.isSiteDropdownOpen;
        },
        selectSite(site) {
            this.localSelectedSite = site;
            this.isSiteDropdownOpen = false;
        },
        clearRating() {
            this.localPoint = -1;
        },
        resetFilters() {
            this.localQuery = "";
            this.localStartDate = null;
            this.localEndDate = null;
            this.localSelectedUser = null;
            this.localSelectedSite = null;
            this.localMultiSelectValue = [];
            this.localDestroyerMultiSelectValue = [];
            this.localStarterMultiSelectValue = [];
            this.localTagsMultiSelectValue = [];
            this.localPoint = undefined;
            this.localTimeSelect = "today";
            this.localFilterDescription = false;
            this.localFilterVideoCall = false;
            this.localFilterLostChats = false;
            this.localFilterClosedByAgent = false;
            
            this.$emit("reset-filters");
            this.closeFilterModal();
        },
        applyFilters() {
            const filters = {
                query: this.localQuery,
                selectedUser: this.localSelectedUser,
                selectedSite: this.localSelectedSite,
                multiSelectValue: this.localMultiSelectValue,
                destroyerMultiSelectValue: this.localDestroyerMultiSelectValue,
                starterMultiSelectValue: this.localStarterMultiSelectValue,
                tagsMultiSelectValue: this.localTagsMultiSelectValue,
                point: this.localPoint,
                startDate: this.localStartDate,
                endDate: this.localEndDate,
                timeSelect: this.localTimeSelect,
                filterDescription: this.localFilterDescription,
                filterVideoCall: this.localFilterVideoCall,
                filterLostChats: this.localFilterLostChats,
                filterClosedByAgent: this.localFilterClosedByAgent,
            };
            
            this.$emit("change", filters);
            this.closeFilterModal();
        },
        hasActiveFilterInTab(tabKey) {
            switch (tabKey) {
                case "quickFilters":
                    return this.localFilterDescription || this.localFilterVideoCall || this.localFilterLostChats || this.localFilterClosedByAgent || (this.localStartDate && this.localEndDate) || (this.localPoint !== undefined && this.localPoint !== null) || this.localSelectedSite;
                case "platforms":
                    return this.localMultiSelectValue.length > 0;
                case "starters":
                    return this.localStarterMultiSelectValue.length > 0;
                case "destroyers":
                    return this.localDestroyerMultiSelectValue.length > 0;
                case "tags":
                    return this.localTagsMultiSelectValue.length > 0;
                case "users":
                    return this.localSelectedUser;
                default:
                    return false;
            }
        },
        getAgentsOptions() {
            let items = this.usersFilter || [];
            items = items.map((item) => ({
                id: item._id,
                label: item.name,
            }));
            items = [
                {
                    id: null,
                    label: this.$t("message.allUsers"),
                },
                {
                    id: "only-chatbot",
                    label: this.$t("message.chatBot"),
                },
                ...items,
            ];
            return items;
        },
        handleClickOutside(event) {
            if (this.isFilterModalOpen && this.$refs.filterPanel && !this.$refs.filterPanel.contains(event.target)
                && !event.target.closest(".custom-filter-button")) {
                this.closeFilterModal();
            }
            
            if (this.isSiteDropdownOpen && !event.target.closest(".custom-select-wrapper")) {
                this.isSiteDropdownOpen = false;
            }
        },
    },
    watch: {
        query(newVal) {
            this.localQuery = newVal;
        },
        startDate(newVal) {
            this.localStartDate = newVal;
        },
        endDate(newVal) {
            this.localEndDate = newVal;
        },
        selectedUser(newVal) {
            this.localSelectedUser = newVal;
        },
        selectedSite(newVal) {
            this.localSelectedSite = newVal;
        },
        multiSelectValue(newVal) {
            this.localMultiSelectValue = [...newVal];
        },
        destroyerMultiSelectValue(newVal) {
            this.localDestroyerMultiSelectValue = [...newVal];
        },
        starterMultiSelectValue(newVal) {
            this.localStarterMultiSelectValue = [...newVal];
        },
        tagsMultiSelectValue(newVal) {
            this.localTagsMultiSelectValue = [...newVal];
        },
        point(newVal) {
            this.localPoint = newVal;
        },
        timeSelect(newVal) {
            this.localTimeSelect = newVal;
        },
        filterDescription(newVal) {
            this.localFilterDescription = newVal;
        },
        filterVideoCall(newVal) {
            this.localFilterVideoCall = newVal;
        },
        filterLostChats(newVal) {
            this.localFilterLostChats = newVal;
        },
        filterClosedByAgent(newVal) {
            this.localFilterClosedByAgent = newVal;
        },
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
    },
};
</script>

<style src="../../../node_modules/vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss" scoped>
#archive-filter-bar {
    .filter-section {
        display: flex;
        align-items: center;
        width: 100%;
    }

    .filter-toolbar {
        display: flex;
        align-items: center;
    }

    .filter-button {
        display: flex;
        align-items: center;
        gap: 10px;
        position: relative;

        .custom-filter-button {
            display: flex;
            align-items: center;
            padding: 6px 12px;
            border-radius: 20px;
            border: 1px solid #e0e0e0;
            background-color: #ffffff;
            transition: all 0.3s ease;
            height: auto;
            position: relative;
            cursor: pointer;

            .button-text {
                font-weight: 500;
                font-size: 14px;
                color: #555;
            }

            .material-icons {
                color: #777;
                transition: color 0.3s ease;
                margin-right: 6px;
            }

            .filter-count {
                width: 18px;
                height: 18px;
                border-radius: 50%;
                background-color: #47499d;
                color: white;
                font-size: 11px;
                font-weight: 600;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-left: 6px;
            }

            &:hover {
                border-color: #bdbdbd;
                background-color: #f9f9f9;
                box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);

                .material-icons {
                    color: #555;
                }
            }

            &.active-filter {
                background-color: #f0f2ff;
                border-color: #47499d;

                .material-icons {
                    color: #47499d;
                }

                .button-text {
                    color: #47499d;
                    font-weight: 600;
                }

                &:hover {
                    background-color: #e6ebff;
                    box-shadow: 0 2px 7px rgba(71, 73, 157, 0.1);
                }
            }
        }
    }

    .filter-panel {
        position: absolute;
        top: calc(100% + 8px);
        width: 800px;
        max-width: 90vw;
        background-color: white;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        z-index: 1000;
        animation: slideDown 0.2s ease-out;
        overflow: hidden;
        margin-top: 2px;

        @keyframes slideDown {
            from {
                opacity: 0;
                transform: translateY(-10px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        .filter-panel-arrow {
            position: absolute;
            top: -10px;
            left: 350px;
            width: 20px;
            height: 10px;
            overflow: hidden;

            &:after {
                content: '';
                position: absolute;
                width: 10px;
                height: 10px;
                background-color: white;
                transform: translateX(5px) translateY(5px) rotate(45deg);
                top: 0;
                left: 0;
                box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
            }
        }

        .filter-panel-content {
            padding: 20px;

            .filter-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 20px;
                border-bottom: 1px solid #f0f0f0;
                padding-bottom: 12px;

                h3 {
                    font-size: 16px;
                    font-weight: 600;
                    color: #333;
                    margin: 0;
                }

                .filter-header-actions {
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    .reset-button {
                        color: #47499d;
                        font-size: 13px;
                        padding: 0 8px;

                        &:hover {
                            color: darken(#47499d, 10%);
                            background: rgba(71, 73, 157, 0.05);
                        }
                    }

                    .apply-button {
                        background-color: #47499d;
                        border-color: #47499d;
                        font-weight: 500;
                        font-size: 13px;
                        height: 28px;
                        padding: 0 12px;

                        &:hover {
                            background-color: darken(#47499d, 10%);
                            border-color: darken(#47499d, 10%);
                        }
                    }

                    .close-button {
                        background: none;
                        border: none;
                        padding: 4px;
                        cursor: pointer;
                        color: #888;
                        transition: color 0.2s;
                        margin-left: 4px;

                        &:hover {
                            color: #47499d;
                        }
                    }
                }
            }

            .filter-tab-layout {
                display: flex;
                gap: 20px;
                min-height: 400px;
                max-height: 450px;

                .filter-tabs {
                    flex: 0 0 200px;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    border-right: 1px solid #f0f0f0;
                    padding-right: 15px;
                    max-height: 450px;
                    overflow: auto;

                    .filter-tab {
                        padding: 10px 16px;
                        border-radius: 4px;
                        cursor: pointer;
                        transition: all 0.3s ease;
                        font-weight: 500;
                        text-align: left;
                        position: relative;
                        padding-left: 15px;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        color: #666;
                        margin-bottom: 4px;

                        &:before {
                            content: '';
                            position: absolute;
                            left: 0;
                            top: 0;
                            bottom: 0;
                            width: 3px;
                            background-color: transparent;
                            transition: background-color 0.3s ease;
                        }

                        .tab-filter-indicator {
                            width: 8px;
                            height: 8px;
                            border-radius: 50%;
                            background-color: #52c41a;
                            margin-left: 8px;
                            display: inline-block;
                            position: relative;
                            animation: pulse 1.5s infinite;
                        }

                        @keyframes pulse {
                            0% {
                                box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.4);
                            }
                            70% {
                                box-shadow: 0 0 0 5px rgba(82, 196, 26, 0);
                            }
                            100% {
                                box-shadow: 0 0 0 0 rgba(82, 196, 26, 0);
                            }
                        }

                        &.active {
                            color: #47499d;
                            background-color: #f5f6ff;
                            font-weight: 600;

                            &:before {
                                background-color: #47499d;
                            }

                            .tab-filter-indicator {
                                background-color: #47499d;
                                animation: pulseActive 1.5s infinite;
                            }

                            @keyframes pulseActive {
                                0% {
                                    box-shadow: 0 0 0 0 rgba(71, 73, 157, 0.4);
                                }
                                70% {
                                    box-shadow: 0 0 0 5px rgba(71, 73, 157, 0);
                                }
                                100% {
                                    box-shadow: 0 0 0 0 rgba(71, 73, 157, 0);
                                }
                            }
                        }

                        &:hover {
                            background-color: #f5f5f5;
                            color: #333;
                        }
                    }
                }

                .filter-tab-content {
                    flex: 1;
                    padding-left: 20px;
                    max-height: 450px;
                    overflow-y: auto;

                    .filter-tab-pane {
                                                    .filter-options {
                                h4 {
                                    margin: 0 0 16px 0;
                                    font-size: 16px;
                                    font-weight: 600;
                                    color: #333;
                                }

                                &:not(:first-child) {
                                    margin-top: 30px;
                                    padding-top: 20px;
                                    border-top: 1px solid #f0f0f0;
                                }
                            }

                        .date-filter-section {
                            .date-controls {
                                margin-bottom: 16px;

                                select {
                                    width: 100%;
                                    padding: 8px 12px;
                                    border: 1px solid #d9d9d9;
                                    border-radius: 6px;
                                    font-size: 14px;
                                    
                                    &:focus {
                                        border-color: #40a9ff;
                                        outline: none;
                                    }
                                }
                            }
                        }

                        .custom-date-inputs {
                            display: flex !important;
                            gap: 16px !important;
                            align-items: flex-end !important;
                            flex-direction: row !important;

                            .date-input-group {
                                flex: 1 !important;
                                min-width: 0 !important;
                                
                                label {
                                    display: block !important;
                                    margin-bottom: 8px !important;
                                    font-weight: 500 !important;
                                    color: #333 !important;
                                    font-size: 14px !important;
                                }
                            }
                        }

                        .platform-grid {
                            display: grid;
                            grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
                            gap: 12px;
                            margin-top: 16px;

                            .platform-card {
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                                gap: 8px;
                                padding: 16px 12px;
                                border: 2px solid #e0e0e0;
                                border-radius: 12px;
                                cursor: pointer;
                                transition: all 0.3s ease;
                                background-color: #fff;
                                min-height: 80px;
                                justify-content: center;

                                &:hover {
                                    border-color: #47499d;
                                    background-color: #f8f9ff;
                                    transform: translateY(-2px);
                                    box-shadow: 0 4px 12px rgba(71, 73, 157, 0.15);
                                }

                                &.selected {
                                    border-color: #47499d;
                                    background-color: #f0f2ff;
                                    
                                    .platform-name {
                                        color: #47499d;
                                        font-weight: 600;
                                    }
                                }

                                .platform-icon {
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;

                                    .platform-icon-svg {
                                        width: 32px;
                                        height: 32px;
                                    }
                                }

                                .platform-name {
                                    font-size: 14px;
                                    font-weight: 500;
                                    color: #333;
                                    text-align: center;
                                }
                            }
                        }

                        .destroyer-grid {
                            display: grid;
                            grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
                            gap: 12px;
                            margin-top: 16px;

                            .destroyer-card {
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                                gap: 8px;
                                padding: 16px 12px;
                                border: 2px solid #e0e0e0;
                                border-radius: 12px;
                                cursor: pointer;
                                transition: all 0.3s ease;
                                background-color: #fff;
                                min-height: 80px;
                                justify-content: center;

                                &:hover {
                                    border-color: #47499d;
                                    background-color: #f8f9ff;
                                    transform: translateY(-2px);
                                    box-shadow: 0 4px 12px rgba(71, 73, 157, 0.15);
                                }

                                &.selected {
                                    border-color: #47499d;
                                    background-color: #f0f2ff;
                                    
                                    .destroyer-name {
                                        color: #47499d;
                                        font-weight: 600;
                                    }

                                    .destroyer-icon-wrapper {
                                        background-color: #47499d;
                                        color: white;

                                        .md-icon {
                                            color: white;
                                        }
                                    }
                                }

                                .destroyer-icon-wrapper {
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    width: 40px;
                                    height: 40px;
                                    border-radius: 8px;
                                    background-color: #f5f5f5;
                                    transition: all 0.3s ease;

                                    .md-icon {
                                        font-size: 20px;
                                        color: #666;
                                    }
                                }

                                .destroyer-name {
                                    font-size: 14px;
                                    font-weight: 500;
                                    color: #333;
                                    text-align: center;
                                }
                            }
                        }

                        .destroyer-option,
                        .tag-option {
                            display: flex;
                            align-items: center;
                            gap: 8px;

                            .destroyer-icon {
                                font-size: 16px;
                                color: #666;
                            }

                            .tag-color {
                                width: 12px;
                                height: 12px;
                                border-radius: 50%;
                            }
                        }

                        .custom-select-wrapper {
                            position: relative;
                            width: 100%;

                            .custom-select {
                                display: flex;
                                align-items: center;
                                justify-content: space-between;
                                padding: 10px 12px;
                                border: 1px solid #e0e0e0;
                                border-radius: 6px;
                                background-color: #fff;
                                cursor: pointer;
                                transition: all 0.2s ease;
                                min-height: 40px;
                                background: none !important;

                                &:hover {
                                    border-color: #47499d;
                                }

                                &.open {
                                    border-color: #47499d;
                                    border-bottom-left-radius: 0;
                                    border-bottom-right-radius: 0;
                                    
                                    .dropdown-arrow {
                                        transform: rotate(180deg);
                                    }
                                }

                                .selected-value {
                                    font-size: 14px;
                                    color: #333;
                                    flex: 1;
                                }

                                .dropdown-arrow {
                                    width: 0 !important;
                                    height: 0 !important;
                                    background: none !important;
                                    border-left: 4px solid transparent !important;
                                    border-right: 4px solid transparent !important;
                                    border-top: 5px solid #666 !important;
                                    border-bottom: none !important;
                                    transition: transform 0.2s ease;
                                    margin-left: 8px;
                                }
                            }

                            .custom-dropdown {
                                position: absolute;
                                top: 100%;
                                left: 0;
                                right: 0;
                                background-color: #fff;
                                border: 1px solid #47499d;
                                border-top: none;
                                border-bottom-left-radius: 6px;
                                border-bottom-right-radius: 6px;
                                max-height: 200px;
                                overflow-y: auto;
                                z-index: 1001;
                                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

                                .dropdown-item {
                                    padding: 10px 12px;
                                    cursor: pointer;
                                    transition: background-color 0.2s ease;
                                    font-size: 14px;
                                    color: #333;
                                    border-bottom: 1px solid #f0f0f0;

                                    &:last-child {
                                        border-bottom: none;
                                    }

                                    &:hover {
                                        background-color: #f8f9ff;
                                    }

                                    &.selected {
                                        background-color: #f0f2ff;
                                        color: #47499d;
                                        font-weight: 600;
                                    }
                                }
                            }
                        }

                        .user-search-wrapper {
                            margin-bottom: 16px;

                            .user-search-input {
                                position: relative;
                                display: flex;
                                align-items: center;

                                .search-icon {
                                    position: absolute;
                                    left: 12px;
                                    color: #999;
                                    font-size: 18px;
                                    z-index: 1;
                                }

                                .search-input {
                                    width: 100%;
                                    padding: 10px 12px 10px 40px;
                                    border: 1px solid #e0e0e0;
                                    border-radius: 6px;
                                    font-size: 14px;
                                    background-color: #fff;
                                    
                                    &:focus {
                                        border-color: #47499d;
                                        outline: none;
                                        box-shadow: 0 0 0 2px rgba(71, 73, 157, 0.1);
                                    }

                                    &::placeholder {
                                        color: #999;
                                    }
                                }
                            }
                        }

                        .users-list {
                            max-height: 300px;
                            overflow-y: auto;
                            border: 1px solid #e0e0e0;
                            border-radius: 6px;
                            background-color: #fff;

                            .user-item {
                                display: flex;
                                align-items: center;
                                padding: 12px 16px;
                                cursor: pointer;
                                transition: all 0.2s ease;
                                border-bottom: 1px solid #f0f0f0;

                                &:last-child {
                                    border-bottom: none;
                                }

                                &:hover {
                                    background-color: #f8f9ff;
                                }

                                &.selected {
                                    background-color: #f0f2ff;
                                    border-left: 3px solid #47499d;
                                    
                                    .user-name {
                                        color: #47499d;
                                        font-weight: 600;
                                    }

                                    .user-avatar {
                                        background-color: #47499d;
                                        color: white;

                                        .material-icons {
                                            color: white;
                                        }
                                    }
                                }

                                .user-avatar {
                                    width: 36px;
                                    height: 36px;
                                    border-radius: 50%;
                                    background-color: #f0f0f0;
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    margin-right: 12px;
                                    font-weight: 600;
                                    font-size: 14px;
                                    color: #666;
                                    transition: all 0.2s ease;

                                    .material-icons {
                                        font-size: 18px;
                                        color: #666;
                                    }

                                    .user-initial {
                                        font-size: 14px;
                                        font-weight: 600;
                                    }
                                }

                                .user-info {
                                    flex: 1;

                                    .user-name {
                                        font-size: 14px;
                                        font-weight: 500;
                                        color: #333;
                                        margin: 0;
                                    }
                                }
                            }
                        }

                        .rating-filter {
                            display: flex;
                            align-items: center;
                            gap: 12px;

                            .question-button {
                                background-color: #f0f0f0;
                                border: 1px solid #d9d9d9;
                                border-radius: 50%;
                                width: 24px;
                                height: 24px;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                cursor: pointer;
                                font-size: 12px;
                                color: #666;

                                &:hover {
                                    background-color: #e6f7ff;
                                    border-color: #40a9ff;
                                    color: #1890ff;
                                }
                            }
                        }

                        .quick-filters-grid {
                            display: grid;
                            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                            gap: 10px;
                            margin-top: 16px;

                            .quick-filter-card {
                                display: flex;
                                align-items: center;
                                gap: 10px;
                                padding: 12px 14px;
                                border: 1px solid #e0e0e0;
                                border-radius: 8px;
                                cursor: pointer;
                                transition: all 0.3s ease;
                                background-color: #fff;
                                min-height: 50px;

                                &:hover {
                                    border-color: #47499d;
                                    background-color: #f8f9ff;
                                    box-shadow: 0 2px 8px rgba(71, 73, 157, 0.1);
                                }

                                &.selected {
                                    border-color: #47499d;
                                    background-color: #f0f2ff;
                                    
                                    .filter-text {
                                        color: #47499d;
                                        font-weight: 600;
                                    }

                                    .filter-icon-wrapper {
                                        background-color: #47499d;
                                        color: white;

                                        .md-icon, .filter-icon {
                                            color: white;
                                        }

                                        .filter-icon {
                                            fill: white;
                                            
                                            * {
                                                fill: white;
                                            }
                                        }
                                    }
                                }

                                .filter-icon-wrapper {
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    width: 36px;
                                    height: 36px;
                                    min-width: 36px;
                                    min-height: 36px;
                                    border-radius: 8px;
                                    background-color: #f5f5f5;
                                    transition: all 0.3s ease;

                                    .md-icon {
                                        font-size: 18px;
                                        color: #666;
                                    }

                                    .filter-icon {
                                        width: 18px;
                                        height: 18px;
                                        fill: #666;
                                        
                                        * {
                                            fill: #666;
                                        }
                                    }
                                }

                                .filter-text {
                                    font-size: 13px;
                                    font-weight: 500;
                                    color: #333;
                                    line-height: 1.3;
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// Multiselect özel stilleri
::v-deep .multiselect__tags {
    min-height: 40px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    
    &:hover {
        border-color: #40a9ff;
    }
}

::v-deep .multiselect__placeholder {
    padding-top: 10px;
    color: #999;
}

::v-deep .multiselect__option--highlight {
    background: #e6f7ff;
    color: #1890ff;
}

::v-deep .multiselect__option--selected {
    background: #1890ff;
    color: white;
}

// Tarih inputları için özel stiller
::v-deep .custom-date-inputs {
    display: flex !important;
    flex-direction: row !important;
    gap: 16px !important;
    
    .date-input-group {
        flex: 1 !important;
        display: flex !important;
        flex-direction: column !important;
        
        .vdatetime-input {
            width: 100% !important;
        }
    }
}
</style> 